import { useSearchParams } from 'react-router-dom'

export const REDIRECT_TO_PARAM = 'redirect_to'

const LOGIN_PATH = '/patient-portal/login'
const DEFAULT_DESTINATION = '/patient-portal/dashboard'
const GOOGLE_REDIRECT_TO_KEY = 'google_redirect_to'

const AUTH_PATHS = [
  '/patient-portal/login',
  '/patient-portal/register',
  '/patient-portal/household',
  '/patient-portal/sendmail',
  '/patient-portal/sendcode',
  '/patient-portal/entercode',
]

const isAuthPath = (pathname: string) => AUTH_PATHS.includes(pathname.replace(/\/$/, ''))

export const sanitizeRedirectTo = (value: string | null | undefined): string | null => {
  if (!value) {
    return null
  }

  let url: URL
  try {
    url = new URL(value, window.location.origin)
  } catch {
    return null
  }

  if (
    url.origin !== window.location.origin ||
    !url.pathname.startsWith('/patient-portal/') ||
    isAuthPath(url.pathname)
  ) {
    return null
  }

  return `${url.pathname}${url.search}`
}

export const appendRedirectTo = (path: string, target: string | null) => {
  if (!target) {
    return path
  }

  const separator = path.includes('?') ? '&' : '?'
  return `${path}${separator}${REDIRECT_TO_PARAM}=${encodeURIComponent(target)}`
}

export const buildLoginUrl = () => {
  const { pathname, search } = window.location
  const target = isAuthPath(pathname)
    ? sanitizeRedirectTo(new URLSearchParams(search).get(REDIRECT_TO_PARAM))
    : sanitizeRedirectTo(`${pathname}${search}`)

  return appendRedirectTo(LOGIN_PATH, target)
}

export const storeGoogleRedirectTo = (target: string | null) => {
  try {
    if (target) {
      sessionStorage.setItem(GOOGLE_REDIRECT_TO_KEY, target)
    } else {
      sessionStorage.removeItem(GOOGLE_REDIRECT_TO_KEY)
    }
  } catch {
    // Storage unavailable: the redirect falls back to the dashboard
  }
}

export const takeGoogleRedirectTo = () => {
  try {
    const stored = sessionStorage.getItem(GOOGLE_REDIRECT_TO_KEY)
    sessionStorage.removeItem(GOOGLE_REDIRECT_TO_KEY)
    return sanitizeRedirectTo(stored)
  } catch {
    return null
  }
}

export const useRedirectTo = () => {
  const [searchParams] = useSearchParams()
  const redirectTo = sanitizeRedirectTo(searchParams.get(REDIRECT_TO_PARAM))

  return {
    redirectTo,
    destination: redirectTo ?? DEFAULT_DESTINATION,
    withRedirectTo: (path: string) => appendRedirectTo(path, redirectTo),
  }
}
